import { useMemo, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { determineTrackType, TRACK_KEYS } from "../utils/trackHelpers";
import { getTrackOrder } from "../utils/trackOrderStorage";
import { getRouteSlug, ROUTE_SLUGS } from "@/shared/utils/bookingUrl";

interface UseTrackSessionsProps {
  selectedLineItems: any[];
  trackSelections: {
    mainTrackSelection: any;
    battleKartSelection: any;
    miniTrackSection: any;
    juniorTrackSelection: any;
    intermediateTrackSelection: any;
  };
  selectedSessionId: string | null;
}

interface TrackData {
  trackType: string;
  trackKey: string;
  sessions: any[];
}

// Map the URL's route slug to a track key.
const TRACK_KEY_BY_ROUTE: Record<string, string> = {
  [ROUTE_SLUGS.BATTLE_KART]: TRACK_KEYS.BK,
  [ROUTE_SLUGS.INTERMEDIATE]: TRACK_KEYS.INTERMEDIATE,
  [ROUTE_SLUGS.JUNIOR]: TRACK_KEYS.JUNIOR,
  [ROUTE_SLUGS.MINI]: TRACK_KEYS.MINI,
  [ROUTE_SLUGS.MAIN]: TRACK_KEYS.MAIN,
  [ROUTE_SLUGS.ARCADE]: TRACK_KEYS.ARCADE,
};

const getTrackKeyFromPath = (pathname: string): string | null => TRACK_KEY_BY_ROUTE[getRouteSlug(pathname) ?? ""] ?? null;

export const useTrackSessions = ({ selectedLineItems, trackSelections, selectedSessionId }: UseTrackSessionsProps) => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(0);
  const previousPathnameRef = useRef<string | null>(null); // Start as null to detect initial mount
  const hasInitializedRef = useRef<boolean>(false);
  const previousSessionIdRef = useRef<string | null>(null); // Track previous selectedSessionId
  const previousTrackKeysRef = useRef<string[]>([]); // Track which tracks were in trackEntries

  // Group sessions by track type
  const sessionsByTrack = useMemo(() => {
    return selectedLineItems.reduce((acc, item) => {
      const { trackType, trackKey } = determineTrackType(item, trackSelections);

      if (!acc[trackKey]) {
        acc[trackKey] = {
          trackType,
          trackKey,
          sessions: [],
        };
      }
      acc[trackKey].sessions.push(item);
      return acc;
    }, {} as Record<string, TrackData>);
  }, [selectedLineItems, trackSelections]);

  // Sort track entries by the order they were added to cart (from localStorage)
  // Note: Track order is updated in useCartData when cart data is fetched/updated
  const trackEntries = useMemo(() => {
    const entries = Object.entries(sessionsByTrack) as [string, TrackData][];
    const storedOrder = getTrackOrder();
    
    return entries.sort((a, b) => {
      const indexA = storedOrder.indexOf(a[0]);
      const indexB = storedOrder.indexOf(b[0]);
      
      // If track is in stored order, use that index
      // If not in stored order (edge case), put at the end
      const safeIndexA = indexA !== -1 ? indexA : 9999;
      const safeIndexB = indexB !== -1 ? indexB : 9999;
      
      return safeIndexA - safeIndexB;
    });
  }, [sessionsByTrack]);

  // Auto-switch to the track matching the current URL path on mount and navigation
  // If the current URL track has no items in cart, default to the last track
  // Also switches when the current URL's track is newly added to trackEntries (after selecting variants)
  useEffect(() => {
    if (trackEntries.length === 0) return;

    const trackKeyFromPath = getTrackKeyFromPath(location.pathname);
    const currentTrackKeys = trackEntries.map(([key]) => key);
    
    // Check if the current URL track was just added to trackEntries
    const urlTrackWasJustAdded = trackKeyFromPath && 
      currentTrackKeys.includes(trackKeyFromPath) && 
      !previousTrackKeysRef.current.includes(trackKeyFromPath);
    
    // On initial mount, set the track based on URL
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousPathnameRef.current = location.pathname;
      previousTrackKeysRef.current = currentTrackKeys;
      
      if (trackKeyFromPath) {
        const trackIndex = trackEntries.findIndex(([key]) => key === trackKeyFromPath);
        if (trackIndex !== -1) {
          // URL track has items in cart, switch to it
          setCurrentPage(trackIndex);
        } else {
          // URL track has no items in cart, default to the last track
          setCurrentPage(trackEntries.length - 1);
        }
      } else {
        // No track detected from URL, default to the last track
        setCurrentPage(trackEntries.length - 1);
      }
      return;
    }

    // Switch if the pathname changed OR if the current URL's track was just added to trackEntries
    const shouldSwitch = location.pathname !== previousPathnameRef.current || urlTrackWasJustAdded;
    
    if (shouldSwitch) {
      previousPathnameRef.current = location.pathname;
      
      if (trackKeyFromPath) {
        const trackIndex = trackEntries.findIndex(([key]) => key === trackKeyFromPath);
        if (trackIndex !== -1) {
          // URL track has items in cart, switch to it
          setCurrentPage(trackIndex);
        } else {
          // URL track has no items in cart, default to the last track
          setCurrentPage(trackEntries.length - 1);
        }
      } else {
        // No track detected from URL, default to the last track
        setCurrentPage(trackEntries.length - 1);
      }
    }
    
    // Always update the previous track keys ref
    previousTrackKeysRef.current = currentTrackKeys;
  }, [location.pathname, trackEntries]);

  // Also switch to the track containing the selected session (only when selectedSessionId actually changes)
  useEffect(() => {
    // Only switch if selectedSessionId actually changed (not just trackEntries)
    if (selectedSessionId !== previousSessionIdRef.current) {
      previousSessionIdRef.current = selectedSessionId;
      
      if (selectedSessionId && trackEntries.length > 0) {
        // Find which track contains the selected session
        const trackIndexWithSession = trackEntries.findIndex(([, trackData]) =>
          trackData.sessions.some((session: any) => session.variantsSelectedObject.id === selectedSessionId)
        );

        // If found, switch to that track
        if (trackIndexWithSession !== -1) {
          setCurrentPage(trackIndexWithSession);
        }
      }
    }
  }, [selectedSessionId, trackEntries]);

  return {
    sessionsByTrack,
    trackEntries,
    currentPage,
    setCurrentPage,
  };
};
